import { useRef, useState } from 'react';
import { VoicePreference } from '../../services/cxone-sdk.service';
import { CXoneState } from '../../types';
import { CxoneSessionRefs, initialCxoneState } from './types';
import { useCxoneSessionDisconnect } from './useCxoneSessionDisconnect';
import { useCxoneSessionInit } from './useCxoneSessionInit';

interface UseCxoneSessionOptions {
  accessToken: string | null;
  cxoneAgentId?: string;
  audioRef: React.RefObject<HTMLAudioElement>;
}

export function useCxoneSession({ accessToken, cxoneAgentId, audioRef }: UseCxoneSessionOptions) {
  const initializedRef = useRef(false);
  const initializingRef = useRef(false);
  const voicePreferenceRef = useRef<VoicePreference | null>(null);
  const previousTokenRef = useRef<string | null>(null);

  const [state, setState] = useState<CXoneState>(initialCxoneState);
  const [voicePreference, setVoicePreference] = useState<VoicePreference | null>(null);
  const [hasSessionConflict, setHasSessionConflict] = useState(false);
  const [needsVoicePreference, setNeedsVoicePreference] = useState(false);

  const refs: CxoneSessionRefs = {
    initializedRef,
    initializingRef,
    voicePreferenceRef,
    previousTokenRef,
  };

  const initializeWithVoicePreference = useCxoneSessionInit({
    accessToken,
    cxoneAgentId,
    audioRef,
    refs,
    setState,
    setVoicePreference,
    setHasSessionConflict,
    setNeedsVoicePreference,
  });

  const { disconnect, clearSessionConflict, resetConnectionState } = useCxoneSessionDisconnect({
    refs,
    setState,
    setVoicePreference,
    setHasSessionConflict,
    setNeedsVoicePreference,
  });

  return {
    state,
    setState,
    voicePreference,
    hasSessionConflict,
    setHasSessionConflict,
    needsVoicePreference,
    setNeedsVoicePreference,
    refs,
    initializeWithVoicePreference,
    disconnect,
    clearSessionConflict,
    resetConnectionState,
  };
}
